export interface ComparisonInput {
  monthlyVolume: number;
  monthlyTransactions: number;
  privateDebitPercent: number;
  privateDebitShare: number;
  privateCreditPercent: number;
  privateCreditShare: number;
  businessPercent: number;
  businessShare: number;
  paymentFixed: number;
  payoutFixed: number;
  payoutPercent: number;
  payoutsPerMonth: number;
  threeDsAvgPerTx: number;
  accountMonthly: number;
  terminalMonthly: number;
  ourPercent: number;
}

export interface RawInput {
  monthlyVolume: number | "";
  monthlyTransactions: number | "";
  privateDebitPercent: number | "";
  privateDebitShare: number | "";
  privateCreditPercent: number | "";
  privateCreditShare: number | "";
  businessPercent: number | "";
  businessShare: number | "";
  paymentFixed: number | "";
  payoutFixed: number | "";
  payoutPercent: number | "";
  payoutsPerMonth: number | "";
  threeDsAvgPerTx: number | "";
  accountMonthly: number | "";
  terminalMonthly: number | "";
  ourPercent: number;
}

export interface CostBreakdown {
  privateDebit: number;
  privateCredit: number;
  business: number;
  fixedFees: number;
  payoutFees: number;
  threeDs: number;
  accountMonthly: number;
  terminalMonthly: number;
  total: number;
  effectivePercent: number;
}

export interface Savings {
  absolute: number;
  percent: number;
}

const isFiniteNonNegative = (v: number | ""): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= 0;

export const isValidInput = (raw: RawInput): raw is ComparisonInput => {
  return (
    isFiniteNonNegative(raw.monthlyVolume) &&
    isFiniteNonNegative(raw.monthlyTransactions) &&
    isFiniteNonNegative(raw.privateDebitPercent) &&
    isFiniteNonNegative(raw.privateDebitShare) &&
    isFiniteNonNegative(raw.privateCreditPercent) &&
    isFiniteNonNegative(raw.privateCreditShare) &&
    isFiniteNonNegative(raw.businessPercent) &&
    isFiniteNonNegative(raw.businessShare) &&
    isFiniteNonNegative(raw.paymentFixed) &&
    isFiniteNonNegative(raw.payoutFixed) &&
    isFiniteNonNegative(raw.payoutPercent) &&
    isFiniteNonNegative(raw.payoutsPerMonth) &&
    isFiniteNonNegative(raw.threeDsAvgPerTx) &&
    isFiniteNonNegative(raw.accountMonthly) &&
    isFiniteNonNegative(raw.terminalMonthly) &&
    Number.isFinite(raw.ourPercent) &&
    raw.ourPercent >= 0
  );
};

export const calculateCompetitorCost = (input: ComparisonInput): CostBreakdown => {
  const shareTotal = input.privateDebitShare + input.privateCreditShare + input.businessShare;
  const wDebit = shareTotal > 0 ? input.privateDebitShare / shareTotal : 0;
  const wCredit = shareTotal > 0 ? input.privateCreditShare / shareTotal : 0;
  const wBusiness = shareTotal > 0 ? input.businessShare / shareTotal : 0;

  const privateDebit = (input.privateDebitPercent / 100) * input.monthlyVolume * wDebit;
  const privateCredit = (input.privateCreditPercent / 100) * input.monthlyVolume * wCredit;
  const business = (input.businessPercent / 100) * input.monthlyVolume * wBusiness;
  const fixedFees = input.paymentFixed * input.monthlyTransactions;

  const payoutFees =
    input.payoutsPerMonth * input.payoutFixed + (input.payoutPercent / 100) * input.monthlyVolume;
  const threeDs = input.threeDsAvgPerTx * input.monthlyTransactions;

  const total =
    privateDebit +
    privateCredit +
    business +
    fixedFees +
    payoutFees +
    threeDs +
    input.accountMonthly +
    input.terminalMonthly;
  const effectivePercent = input.monthlyVolume > 0 ? (total / input.monthlyVolume) * 100 : 0;

  return {
    privateDebit,
    privateCredit,
    business,
    fixedFees,
    payoutFees,
    threeDs,
    accountMonthly: input.accountMonthly,
    terminalMonthly: input.terminalMonthly,
    total,
    effectivePercent,
  };
};

export const calculateOurCost = (input: ComparisonInput): CostBreakdown => {
  const total = (input.ourPercent / 100) * input.monthlyVolume;
  const effectivePercent = input.monthlyVolume > 0 ? (total / input.monthlyVolume) * 100 : 0;
  return {
    privateDebit: 0,
    privateCredit: 0,
    business: 0,
    fixedFees: 0,
    payoutFees: 0,
    threeDs: 0,
    accountMonthly: 0,
    terminalMonthly: 0,
    total,
    effectivePercent,
  };
};

export const calculateSavings = (competitor: CostBreakdown, ours: CostBreakdown): Savings => {
  const absolute = competitor.total - ours.total;
  const percent = competitor.total > 0 ? (absolute / competitor.total) * 100 : 0;
  return { absolute, percent };
};
